import React, { useEffect } from "react";
import BulkAddPartsForm from "@/components/BulkAddPartsForm";
import { motion } from "framer-motion";
import { usePartsQuota } from "../hooks/usePartsQuota";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useToast } from "../components/ui/use-toast";
import { openAuthDialog } from "../store/auth/authSlice";
import { Loader2, LogIn, UserX } from "lucide-react";
import { getEffectivePlan } from "../utils/subscriptionUtils";

const BulkAddPartsPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { toast } = useToast();
  const {
    isLoading: isAuthLoading,
    isAuthenticated,
    user,
    currentSubscription,
  } = useSelector((state) => state.auth);
  const canAccess = getEffectivePlan(currentSubscription) === "premium";

  const { canAddParts, partsLimit, totalParts } = usePartsQuota();

  useEffect(() => {
    if (isAuthLoading) return;
    if (!isAuthenticated) {
      navigate("/");
      dispatch(
        openAuthDialog({
          showLoginFirstOrOnSuccess: true,
          redirectPage: window.location.pathname,
          onSuccessfulAuth: () => navigate("/dashboard", { replace: true }),
          defaultTabOverride: "login",
        }),
      );
      return;
    }

    if (user?.role !== "seller") {
      toast({
        title: "Access Denied",
        description:
          "This page is for sellers only. Redirecting to your profile...",
        variant: "destructive",
        duration: 3000,
      });
      navigate(user.role === "buyer" ? "/profile" : "/");
      return;
    }

    if (currentSubscription?.status !== "active") {
      toast({
        title: "Membership Required",
        description:
          "Please select a membership plan to access your dashboard.",
        variant: "destructive",
        duration: 5000,
      });
      navigate("/list-inventory");
      return;
    }

    if (!canAccess) {
      toast({
        variant: "destructive",
        title: "Access Denied",
        description:
          "Only premium members can bulk add parts. Please upgrade your membership.",
        duration: 5000,
      });
      navigate("/list-inventory");
      return;
    }

    if (!canAddParts) {
      toast({
        variant: "destructive",
        title: "Quota Exceeded",
        description:
          partsLimit === "unlimited"
            ? "Please subscribe to continue."
            : `Your plan allows up to ${partsLimit} parts. You already have ${totalParts}.`,
        duration: 5000,
      });
      navigate("/dashboard");
      return;
    }
  }, [
    isAuthLoading,
    isAuthenticated,
    user,
    canAddParts,
    partsLimit,
    totalParts,
    navigate,
  ]);

  if (isAuthLoading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  if (
    !isAuthenticated ||
    user?.role !== "seller" ||
    getEffectivePlan(currentSubscription) !== "premium" ||
    currentSubscription?.status !== "active" ||
    !canAddParts
  ) {
    return (
      <div className="container mx-auto py-10 px-4 text-center min-h-screen flex flex-col justify-center items-center bg-gradient-to-br from-background to-secondary/5">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="p-8 rounded-xl bg-card shadow-2xl max-w-md w-full"
        >
          {isAuthenticated ? (
            <UserX className="h-12 w-12 text-destructive mx-auto mb-4" />
          ) : (
            <LogIn className="h-12 w-12 text-primary mx-auto mb-4" />
          )}
          <h1 className="text-2xl font-bold text-foreground mb-3">
            {isAuthenticated ? "Access Denied" : "Authentication Required"}
          </h1>
          <p className="text-muted-foreground">
            {isAuthenticated
              ? "This page is only available for premium sellers. Please upgrade your membership to continue."
              : "Please log in or sign up to continue."}
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      className="w-full"
    >
      <h1 className="text-3xl font-bold mb-6 text-center bg-gradient-to-r from-primary to-accent text-transparent bg-clip-text">
        Add Parts in Bulk
      </h1>
      <div className="max-w-2xl mx-auto">
        <BulkAddPartsForm />
      </div>
    </motion.div>
  );
};

export default BulkAddPartsPage;
